import { Box, CircularProgress, TextField, Typography } from "@mui/material";
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { useContext, useEffect, useState } from "react";
import AuthContext from "../../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import Fetch from "../../services/Fetch";
import GridMediaCard from "../../components/cards/GridMediaCard";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import PageHero from "../../components/motion/PageHero";
import { resolveCourseImage } from "../../helper/resolveMediaUrl";
import { useConstants } from "../../hooks/UseConstants";
import { formatCurrency } from "../../helper/formatCurrency";

function parseList(result) {
    if (result?.status !== 200) return [];
    const payload = result.data?.data;
    return Array.isArray(payload) ? payload : (payload?.data || []);
}

function Courses() {
    const { host, language } = useConstants();
    const { user } = useContext(AuthContext);
    const isAr = language === 'ar';
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const categoryId = params.get('category_id') || '';
    const [loading, setLoading] = useState(true);
    const [courses, setCourses] = useState([]);
    const [search, setSearch] = useState('');

    const load = async (term = search) => {
        setLoading(true);
        let url = `${host}/courses?per_page=12&page=1&status[]=accepted`;
        if (user?.major?.id) url += '&for_user=1';
        if (categoryId) url += `&category_id=${categoryId}`;
        if (term) url += `&search_by_course_name=${encodeURIComponent(term)}`;
        const result = await Fetch(url, 'GET');
        setCourses(parseList(result));
        setLoading(false);
    };

    useEffect(() => { load(''); }, [host, categoryId, user?.major?.id]);

    const priceLabel = (course) => {
        if (course.is_free) return isAr ? 'مجاني' : 'Free';
        return formatCurrency(course.effective_price ?? course.price, isAr);
    };

    return (
        <Box>
            <PageHero
                title={isAr ? 'استكشاف الدورات' : 'Explore courses'}
                subtitle={isAr ? 'تعلّم مهارات جديدة من أفضل المدرسين' : 'Learn new skills from top instructors'}
                icon={<MenuBookOutlinedIcon />}
            />

            <ScrollReveal>
            <DashboardPanelCard className="mx-0 mb-4">
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder={isAr ? 'بحث...' : 'Search...'}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <button className="bg-blue-color text-white px-4 rounded-lg shrink-0" onClick={() => load(search)}>
                        {isAr ? 'بحث' : 'Search'}
                    </button>
                </Box>
            </DashboardPanelCard>
            </ScrollReveal>

            {loading ? (
                <Box className="flex justify-center py-16"><CircularProgress size={70} /></Box>
            ) : (
                <Box className="grid grid-cols-3 gap-5 max-sm:grid-cols-1 justify-items-center">
                    {!courses.length ? (
                        <Typography className="col-span-3 text-center py-12 text-gray-500">
                            {isAr ? 'لا توجد دورات متاحة حالياً' : 'No courses available yet'}
                        </Typography>
                    ) : null}
                    {courses.map((course, i) => (
                        <ScrollReveal key={course.id} delay={Math.min(i * 60, 360)} direction="up" className="w-full max-w-sm">
                            <GridMediaCard
                                image={resolveCourseImage(course)}
                                title={isAr ? course.name_ar || course.name_en : course.name_en || course.name_ar}
                                description={
                                    course.teacher
                                        ? `${course.teacher.first_name} ${course.teacher.last_name}`
                                        : priceLabel(course)
                                }
                                onClick={() => navigate(`/courses/${course.id}`)}
                                footer={
                                    <Typography variant="caption" className="text-blue-600 font-bold">
                                        {priceLabel(course)}
                                    </Typography>
                                }
                            />
                        </ScrollReveal>
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default Courses;
